/**
 * Cart Rule Engine — shared between multi-vendor-carts.js and brand-cart-drawer.js
 *
 * A brand_cart_rule activates when any cart item for a given vendor has a
 * product tag equal to the rule's `triggerTag` (case-insensitive). When multiple
 * rules match, the highest priority wins; ties resolve to list order.
 *
 * Because Shopify's /cart.js response does not include product tags, we lazily
 * fetch `/products/{handle}.js` for any cart item whose handle isn't in the
 * cache yet. Tags are stored lowercased for O(1) lookup.
 *
 * Exposed on `window.CartRuleEngine` so both managers share one cache.
 */
(function (global) {
  'use strict';

  const tagCache = new Map();     // handle -> Set<string> (lowercase tags)
  const pending = new Map();      // handle -> Promise<Set<string>>

  async function loadTags(handle) {
    if (!handle) return new Set();
    if (tagCache.has(handle)) return tagCache.get(handle);
    if (pending.has(handle)) return pending.get(handle);

    const promise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((p) => {
        const tags = new Set((p.tags || []).map((t) => String(t).toLowerCase()));
        tagCache.set(handle, tags);
        pending.delete(handle);
        return tags;
      })
      .catch((err) => {
        console.warn('[CartRuleEngine] Failed to load tags for', handle, err);
        tagCache.set(handle, new Set());
        pending.delete(handle);
        return new Set();
      });

    pending.set(handle, promise);
    return promise;
  }

  /**
   * Ensure tags are cached for every item in `cartItems` (each must have a
   * `handle` field, as returned by /cart.js). Resolves when all fetches done.
   */
  async function primeTags(cartItems) {
    const handles = [...new Set((cartItems || []).map((i) => i?.handle).filter(Boolean))];
    await Promise.all(handles.map(loadTags));
  }

  /**
   * Return true if any item in `cartItems` whose `vendor` equals `vendor` has a
   * tag equal to `triggerTag` (case-insensitive). Expects tags to be cached —
   * call `primeTags(cartItems)` first (or use async wrappers below).
   */
  function vendorHasTag(cartItems, vendor, triggerTag) {
    if (!triggerTag) return false;
    const needle = String(triggerTag).toLowerCase();
    for (const item of cartItems || []) {
      if (item.vendor !== vendor) continue;
      const tags = tagCache.get(item.handle);
      if (tags && tags.has(needle)) return true;
    }
    return false;
  }

  /**
   * Given an array of cart rules (shape: { triggerTag, minimum, opening, priority }),
   * plus current cart items and a vendor, resolve the effective minimum and
   * opening amounts. Falls back to the provided `baseMinimum` / `baseOpening`
   * when no rule matches or the matching rule's field is null.
   *
   * Returns: { minimum: number, opening: number, matchedRule: rule|null }
   */
  function resolveForVendor({ rules, cartItems, vendor, baseMinimum, baseOpening }) {
    let matched = null;
    for (const rule of rules || []) {
      if (!vendorHasTag(cartItems, vendor, rule.triggerTag)) continue;
      const priority = Number(rule.priority) || 0;
      const currentPriority = matched ? Number(matched.priority) || 0 : -Infinity;
      if (priority > currentPriority) matched = rule;
    }
    return {
      minimum: matched && matched.minimum != null ? matched.minimum : baseMinimum,
      opening: matched && matched.opening != null ? matched.opening : baseOpening,
      matchedRule: matched,
    };
  }

  global.CartRuleEngine = { loadTags, primeTags, vendorHasTag, resolveForVendor };
})(window);
